import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton
} from '@mui/material';
import {
  EditNote,
  Speed,
  Palette,
  TrendingUp,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import {
  landingFeatureCardSx,
  landingFeatureIconSx,
  landingFeatureBadgeSx,
  authAccent,
} from './authStyles';

const MobileFeatureCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const features = [
    {
      icon: <EditNote sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Writing',
      description: 'Transform your experience into compelling achievements with intelligent content generation',
      highlight: '10x faster writing'
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'No Subscription',
      description: 'Create unlimited resumes for free. Only pay when you download. One payment = 90-day access',
      highlight: 'Pay per download'
    },
    {
      icon: <Palette sx={{ fontSize: 40 }} />,
      title: 'ATS-Optimized',
      description: 'Pass applicant tracking systems with professionally designed, recruiter-approved templates',
      highlight: '95% pass rate'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: 'Unlimited Editing',
      description: 'Create and edit as many resumes as you want. Perfect them before you pay a single cent',
      highlight: 'Free to create'
    }
  ];

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        mb: 8
      }}
    >
      {/* Carousel Container */}
      <Box
        sx={{ 
          overflow: 'hidden',
          height: 280
        }}
      >
        <Box
          sx={{ 
            display: 'flex',
            height: '100%',
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
        >
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                minWidth: '100%',
                height: '100%',
                px: 0.5
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  ...landingFeatureCardSx,
                  borderRadius: 3,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ ...landingFeatureIconSx, mb: 1.5 }}>
                  {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                </Box>
                <Box
                  sx={{
                    ...landingFeatureBadgeSx,
                    px: 2,
                    py: 0.6,
                    mb: 1.5,
                    mx: 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ color: authAccent, fontWeight: 700, fontSize: '0.85rem' }}>
                    {feature.highlight}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.15rem' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem', lineHeight: 1.5, px: 2 }}>
                  {feature.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          mb: 1.5
        }}
      >
        <IconButton
          onClick={handlePrevSlide}
          disabled={currentSlide === 0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            width: 48,
            height: 48,
            '&:hover': {
              bgcolor: 'rgba(129, 140, 248, 0.3)',
              borderColor: authAccent,
              transform: 'scale(1.05)'
            },
            '&:disabled': {
              bgcolor: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.3)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <ChevronLeft />
        </IconButton>

        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255,255,255,0.8)',
            fontWeight: 600,
            fontSize: '0.9rem',
            px: 2
          }}
        >
          {currentSlide + 1} / {features.length}
        </Typography>

        <IconButton
          onClick={handleNextSlide}
          disabled={currentSlide === features.length - 1}
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            width: 48,
            height: 48,
            '&:hover': {
              bgcolor: 'rgba(129, 140, 248, 0.3)',
              borderColor: authAccent,
              transform: 'scale(1.05)'
            },
            '&:disabled': {
              bgcolor: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.3)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Navigation Dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
        {features.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentSlide(index)}
            sx={{
              width: currentSlide === index ? 32 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: currentSlide === index ? authAccent : 'rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: currentSlide === index ? authAccent : 'rgba(255,255,255,0.5)',
                transform: 'scale(1.1)'
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MobileFeatureCarousel;

